import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  DollarSign, 
  TrendingUp, 
  Calculator, 
  Trophy,
  AlertTriangle,
  Info,
  Zap,
  Target,
  Clock,
  Users,
  Star
} from 'lucide-react';
import useAutoScroll from '../../hooks/useAutoScroll';

// Types
interface StakeTier {
  id: string;
  name: string;
  minAmount: number;
  maxAmount: number;
  color: string;
  difficulty: 'easy' | 'medium' | 'hard';
  questionCount: number;
  timePerQuestion: number;
  features: string[];
  recommendedFor: string[];
}

interface PrizeCalculation {
  stake: number;
  appFee: number;
  prize: number;
  totalPot: number;
  difficulty: string;
  questionCount: number;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: #ffd700;
`;

const Description = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const StakeInputSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BalanceInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const BalanceAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #4caf50;
  margin-bottom: 5px;
`;

const BalanceLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StakeInputContainer = styled.div`
  margin-bottom: 25px;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: white;
`;

const StakeInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 15px 20px;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #4caf50;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const QuickAmounts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 10px;
  margin-top: 15px;
`;

const QuickAmountButton = styled.button<{ isSelected: boolean; amount: number }>`
  background: ${props => props.isSelected 
    ? 'linear-gradient(45deg, #4caf50, #8bc34a)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border: 1px solid ${props => props.isSelected 
    ? '#4caf50' 
    : 'rgba(255, 255, 255, 0.2)'
  };
  color: white;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  
  &:hover {
    transform: scale(1.05);
    background: ${props => props.isSelected 
      ? 'linear-gradient(45deg, #4caf50, #8bc34a)' 
      : 'rgba(255, 255, 255, 0.2)'
    };
  }
`;

const PrizeCalculationSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const PrizeCard = styled.div<{ isVisible: boolean }>`
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 25px;
  text-align: center;
  transform: ${props => props.isVisible ? 'scale(1)' : 'scale(0.9)'};
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: all 0.5s ease;
`;

const PrizeAmount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 10px;
`;

const PrizeLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CalculationDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
    font-weight: 600;
    font-size: 1.1rem;
    color: #ffd700;
  }
`;

const DetailLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
`;

const DetailValue = styled.div`
  font-weight: 600;
  color: white;
`;

const CompetitionInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 20px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const InfoTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
`;

const InfoItem = styled.div`
  text-align: center;
`;

const InfoValue = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  margin-bottom: 5px;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StakeTiersSection = styled.div`
  margin-top: 30px;
`;

const TiersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
`;

const TierCard = styled.div<{ isActive: boolean; color: string }>`
  background: ${props => props.isActive 
    ? 'rgba(255, 255, 255, 0.15)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.isActive 
    ? props.color 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TierName = styled.div`
  font-weight: 600;
  margin-bottom: 10px;
  color: ${props => props.color};
`;

const TierRange = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
`;

const TierFeatures = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'linear-gradient(45deg, #4caf50, #8bc34a)'
  };
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  align-self: center;
  margin-top: 20px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(76, 175, 80, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const StakeSetting: React.FC<{
  onConfirm: (stake: number) => void;
  onBack: () => void;
  userBalance: number;
  selectedCategory: string;
  selectedMode: string;
}> = ({ onConfirm, onBack, userBalance, selectedCategory, selectedMode }) => {
  const [stakeAmount, setStakeAmount] = useState<number>(0);
  const [prizeCalculation, setPrizeCalculation] = useState<PrizeCalculation | null>(null);
  const { scrollToContinueButton } = useAutoScroll();

  const stakeTiers: StakeTier[] = [
    {
      id: 'beginner',
      name: 'Beginner',
      minAmount: 1,
      maxAmount: 10,
      color: '#4caf50',
      difficulty: 'easy',
      questionCount: 10,
      timePerQuestion: 30,
      features: ['Multiple Choice', 'Basic Questions', 'Quick Rounds'],
      recommendedFor: ['New Users', 'Students', 'Practice']
    },
    {
      id: 'intermediate',
      name: 'Intermediate',
      minAmount: 50,
      maxAmount: 99,
      color: '#ff9800',
      difficulty: 'medium',
      questionCount: 25,
      timePerQuestion: 45,
      features: ['Mixed Questions', 'Time Pressure', 'Skill Testing'],
      recommendedFor: ['Regular Users', 'Skill Building', 'Competition']
    },
    {
      id: 'advanced',
      name: 'Advanced',
      minAmount: 100,
      maxAmount: 500,
      color: '#f44336',
      difficulty: 'hard',
      questionCount: 30,
      timePerQuestion: 60,
      features: ['Complex Problems', 'Extended Time', 'High Stakes'],
      recommendedFor: ['Experienced Users', 'Professionals', 'High Rewards']
    }
  ];

  const quickAmounts = [10, 25, 50, 100, 250, 500];

  useEffect(() => {
    if (stakeAmount > 0) {
      calculatePrize(stakeAmount);
    }
  }, [stakeAmount]);

  const calculatePrize = (stake: number) => {
    const appFee = stake * 0.1; // 10% app fee
    const prize = stake * 2 - appFee; // Double stake minus fee
    const totalPot = stake * 2;
    
    let difficulty = 'easy';
    let questionCount = 10;
    
    if (stake >= 50 && stake <= 99) {
      difficulty = 'medium';
      questionCount = 25;
    } else if (stake >= 100) {
      difficulty = 'hard';
      questionCount = 30;
    }
    
    setPrizeCalculation({
      stake,
      appFee,
      prize,
      totalPot,
      difficulty,
      questionCount
    });
  };

  const handleStakeChange = (value: string) => {
    const amount = parseInt(value) || 0;
    setStakeAmount(amount);
  };

  const handleQuickAmount = (amount: number) => {
    setStakeAmount(amount);
    
    // Auto-scroll to continue button after quick amount selection
    setTimeout(() => {
      scrollToContinueButton({ 
        behavior: 'smooth', 
        block: 'center',
        offset: 50 
      });
    }, 200);
  };

  const handleContinue = () => {
    if (stakeAmount > 0 && stakeAmount <= userBalance) {
      onConfirm(stakeAmount);
    }
  };

  const getActiveTier = () => {
    return stakeTiers.find(tier => 
      stakeAmount >= tier.minAmount && stakeAmount <= tier.maxAmount
    );
  };

  const isStakeValid = stakeAmount > 0 && stakeAmount <= userBalance;

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <ArrowLeft size={24} />
        </BackButton>
        <div>
          <Title>Set Your Stake</Title>
          <Description>
            Choose how much you want to stake in this competition. 
            Higher stakes mean more questions, higher difficulty, and bigger prizes!
          </Description>
        </div>
      </Header>

      <ContentGrid>
        <StakeInputSection>
          <SectionTitle>
            <DollarSign />
            Stake Amount
          </SectionTitle>
          
          <BalanceInfo>
            <BalanceAmount>KES {userBalance.toLocaleString()}</BalanceAmount>
            <BalanceLabel>Available Balance</BalanceLabel>
          </BalanceInfo>
          
          <StakeInputContainer>
            <InputLabel>Enter Stake Amount (KES)</InputLabel>
            <StakeInput
              type="number"
              value={stakeAmount || ''}
              onChange={(e) => handleStakeChange(e.target.value)}
              placeholder="Enter amount..."
              min="1"
              max={userBalance}
            />
            
            <QuickAmounts>
              {quickAmounts.map((amount) => (
                <QuickAmountButton
                  key={amount}
                  isSelected={stakeAmount === amount}
                  amount={amount}
                  onClick={() => handleQuickAmount(amount)}
                >
                  {amount}
                </QuickAmountButton>
              ))}
            </QuickAmounts>
          </StakeInputContainer>
          
          {stakeAmount > userBalance && (
            <div style={{ 
              color: '#f44336', 
              fontSize: '0.9rem', 
              textAlign: 'center',
              padding: '10px',
              background: 'rgba(244, 67, 54, 0.1)',
              borderRadius: '10px',
              border: '1px solid rgba(244, 67, 54, 0.3)'
            }}>
              <AlertTriangle size={16} style={{ marginRight: '8px' }} />
              Stake amount exceeds your balance
            </div>
          )}
        </StakeInputSection>

        <PrizeCalculationSection>
          <SectionTitle>
            <Trophy />
            Prize Calculation
          </SectionTitle>
          
          {prizeCalculation && (
            <>
              <PrizeCard isVisible={!!prizeCalculation}>
                <PrizeAmount>KES {prizeCalculation.prize.toLocaleString()}</PrizeAmount>
                <PrizeLabel>Potential Prize</PrizeLabel>
              </PrizeCard>
              
              <CalculationDetails>
                <DetailRow>
                  <DetailLabel>Your Stake</DetailLabel>
                  <DetailValue>KES {prizeCalculation.stake.toLocaleString()}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>App Fee (10%)</DetailLabel>
                  <DetailValue>KES {prizeCalculation.appFee.toLocaleString()}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Total Pot</DetailLabel>
                  <DetailValue>KES {prizeCalculation.totalPot.toLocaleString()}</DetailValue>
                </DetailRow>
              </CalculationDetails>
              
              <CompetitionInfo>
                <InfoTitle>
                  <Info />
                  Competition Details
                </InfoTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoValue>{prizeCalculation.difficulty}</InfoValue>
                    <InfoLabel>Difficulty</InfoLabel>
                  </InfoItem>
                  <InfoItem>
                    <InfoValue>{prizeCalculation.questionCount}</InfoValue>
                    <InfoLabel>Questions</InfoLabel>
                  </InfoItem>
                  <InfoItem>
                    <InfoValue>{prizeCalculation.questionCount * 30}s</InfoValue>
                    <InfoLabel>Total Time</InfoLabel>
                  </InfoItem>
                </InfoGrid>
              </CompetitionInfo>
            </>
          )}
        </PrizeCalculationSection>
      </ContentGrid>

      <StakeTiersSection>
        <SectionTitle>
          <Target />
          Stake Tiers
        </SectionTitle>
        <TiersGrid>
          {stakeTiers.map((tier) => {
            const isActive = getActiveTier()?.id === tier.id;
            return (
              <TierCard
                key={tier.id}
                isActive={isActive}
                color={tier.color}
              >
                <TierName style={{ color: tier.color }}>{tier.name}</TierName>
                <TierRange>KES {tier.minAmount} - {tier.maxAmount}</TierRange>
                <TierFeatures>
                  {tier.features.join(' • ')}
                </TierFeatures>
              </TierCard>
            );
          })}
        </TiersGrid>
      </StakeTiersSection>

      <ContinueButton
        disabled={!isStakeValid}
        onClick={handleContinue}
      >
        Continue to Waiting Room
      </ContinueButton>
    </Container>
  );
};

export default StakeSetting;
